import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
} from '../../style-utils/theme';
import { down, up } from '../../style-utils/media-queries';
import { shimmer, skeletonBackground } from '../../style-utils/motion';
import { m, StateSwitch } from '../../motion';

/**
 * ═════════ ONE COLUMN WIDTH FOR EVERY BLOCK ON THIS SCREEN ═════════
 *
 * THE DEFECT THIS ENDS. ContentButtonsWrapper is `align-items: center`, and the
 * three blocks stacked in it did not agree about how wide they were:
 *
 *   DeskArea      width:100%  → full bleed, and the grids INSIDE it capped at
 *                               720px, so they sat hard against its LEFT edge
 *   FeedSlot      max-width:720px → centred by the wrapper
 *
 * Below 720px of content column both fill it and nothing is visible. Above it
 * the two boxes drift apart by (column - 720) / 2 — so on a desk monitor the
 * Shift Monitor tile no longer lined up with the feed heading under it, which is
 * exactly what was reported. Every block that stacks here now measures itself
 * against THIS, so there is one left edge and one right edge on the screen.
 *
 * IT GROWS WITH THE SCREEN. 720px on a 1440px display left the desk using half
 * its monitor with the other half blank. `width: 100%` still caps it, so a step
 * only ever applies when the column actually has that room — at 1280px of
 * VIEWPORT the content column is nearer 640px and the xl step does nothing.
 */
const deskColumn = `
  width: 100%;
  max-width: 720px;

  ${up('xl')} {
    max-width: 880px;
  }

  ${up('xxl')} {
    max-width: 1040px;
  }
`;

/**
 * THE MAIN MENU'S OWN LAYER.
 *
 * The grouping chrome that used to live here — MenuSection, its eyebrow heading
 * and its hint line — is gone with the groups themselves. The menu is a flat
 * list of buttons now, so a heading above a subset of them would be a label for
 * a thing that no longer exists.
 *
 * Everything below follows from one sentence: the person using this is standing
 * at a counter, holding something, with somebody waiting.
 */

/**
 * ═══════════ THE BOARD — one grid, three cells, and the clock never moves ═══
 *
 * The menu used to be two flex columns (NavbarContainer + ContentAndButtons-
 * Container) and the gate a third layout inside the second column. The shift
 * clock lived in the title column on shift and in the gate's centred slot off
 * shift, which meant the card was UNMOUNTED AND REBUILT at the exact moment it
 * was doing its most important work: ShiftClock's mount effect drops the
 * handover receipt (shift-clock.tsx, setReceipt(null) on mount), so clocking
 * out from the menu rebuilt the card and took the receipt with it.
 *
 * One grid instead. The clock has ONE cell, at ONE position in the React tree,
 * and only the grid-template-areas around it change:
 *
 *   menu (>= md)            menu (< md)      gate / pick / frozen
 *   ┌───────┬─────────┐     ┌─────────┐      ┌─────────┐
 *   │ title │  body   │     │  title  │      │  body   │   <- GateHead, cards
 *   │ clock │  body   │     │  clock  │      │  clock  │   <- the same card
 *   │       │  body   │     │  body   │      └─────────┘
 *   └───────┴─────────┘     └─────────┘
 *
 * Column widths replicate what NavbarContainer isMainMenu used to give the
 * title column: 4fr against 6fr, capped to a fixed basis from lg up (the note
 * in layout-container.styled.tsx explains why the cap exists). The body cell
 * scrolls on its own on a desk, as the old content column did, so the title
 * and the clock stay put while the feed scrolls under them.
 *
 * NO BACKTICKS IN THESE COMMENTS: styled template literals.
 */
const menuAreas = `
  grid-template-columns: minmax(0, 4fr) minmax(0, 6fr);
  grid-template-rows: auto auto minmax(0, 1fr);
  grid-template-areas:
    'title body'
    'clock body'
    '.     body';

  ${up('lg')} {
    grid-template-columns: 320px minmax(0, 1fr);
  }

  ${up('xxl')} {
    grid-template-columns: 380px minmax(0, 1fr);
  }

  ${down('md')} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    grid-template-areas:
      'title'
      'clock'
      'body';
  }

  /* STACKED ON THE BOARD'S OWN WIDTH TOO, NOT ONLY THE VIEWPORT'S.
     On the desk the board sits beside the rail and the building panel, so the
     viewport over-states it: an iPad in portrait (810) leaves the board 424px
     and the 768 kiosk 382px, both ABOVE md, so the 4fr/6fr split handed the
     shift card about 140px — "Clock out & hand over" ran across the next card
     and "Who's home" printed one letter per line (persona journey J-0.3,
     2026-09-25). Below 600px of board the three cells stack exactly as they do
     on a phone, and the board scrolls as one column. */
  @container menuboard (max-width: 599px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    grid-template-areas:
      'title'
      'clock'
      'body';
    align-content: start;
    overflow-y: auto;
  }
`;

const gateAreas = `
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto;
  grid-template-areas:
    'body'
    'clock';
  row-gap: ${spacing.sm};
  /* CENTRED WHEN IT FITS, TOP-ALIGNED WHEN IT DOES NOT.
     Plain 'center' overflows EQUALLY in both directions, and the overflow above
     the container cannot be scrolled to — so on a short window, or a gate
     carrying a long handover, the heading was pushed off the top of the screen
     with no way to reach it. 'safe' falls back to start alignment exactly when
     that would happen. The unprefixed line above it is the fallback for a
     browser that does not know the keyword, which behaves as it does today. */
  align-content: center;
  align-content: safe center;
  justify-items: center;
  padding: ${spacing.xxl} ${spacing.lg};

  ${down('md')} {
    padding: ${spacing.md} ${spacing.sm};
  }
`;

/**
 * THE CONTAINER THE BOARD MEASURES ITSELF AGAINST — see the note in menuAreas.
 * A wrapper the menu owns, for the reason DeskArea gives: the shared
 * NavbarAndContentContainer must not become a containment context. It only
 * passes the flex column through, so MenuBoard's flex: 1 and the body cell's
 * own scrolling behave exactly as before.
 */
export const MenuBoardFrame = styled.div`
  container-type: inline-size;
  container-name: menuboard;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
`;

export const MenuBoard = styled.div<{ $mode: 'menu' | 'gate' }>`
  display: grid;
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 0;
  ${({ $mode }) => ($mode === 'gate' ? gateAreas : menuAreas)}
`;

/* The title column: the "Main Menu" heading, from the shared Navbar. */
export const TitleCell = styled.div`
  grid-area: title;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/**
 * THE BODY CELL — everything that changes with the state (the tiles, the
 * picker, the gate's heading, the frozen notice) crossfades inside it.
 *
 * On a desk it scrolls on its own, exactly as the old content column did
 * (ContentAndButtonsContainer overflow: auto); under md the page scrolls.
 * In gate mode it is the 560px centred column the gate always had.
 */
export const BodyCell = styled.div<{ $mode: 'menu' | 'gate' }>`
  grid-area: body;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 0;

  ${({ $mode }) =>
    $mode === 'gate'
      ? /* min-height: auto — UNDOING THE `min-height: 0` ABOVE, AND THE REASON
           THE GATE HEADING PRINTED ON TOP OF THE SHIFT CARD.

           Measured on the running app: the gate grid computed
           `grid-template-rows: 0px 704px`. The body row was ZERO while the
           heading inside it was 98px tall, so "Start your shift" and its
           paragraph overflowed downwards and were painted across the top of the
           clock card — two blocks of text on the same pixels, on the one screen
           that has nothing else on it.

           `min-height: 0` is correct for MENU mode and is there for the usual
           reason: a flex/grid child that scrolls needs its automatic minimum
           size removed or it refuses to shrink. On the gate there is no
           scrolling child, and the same declaration removes the automatic
           minimum that an `auto` grid row uses to size itself — so the row
           collapses to nothing and its content overflows instead of pushing the
           next row down.

           Proved by substitution in the live page: setting `min-height: auto`
           on this cell (or `align-self: start`, or `min-content` rows) takes the
           row from 0px to 98px; changing `align-content` does not. */
        `max-width: 560px;
         min-height: auto;`
      : `padding: ${spacing.xxl} ${spacing.lg} 0;
         overflow-y: auto;

         ${down('md')} {
           padding: ${spacing.md} ${spacing.sm} 0;
           overflow: visible;
         }

         @container menuboard (max-width: 599px) {
           padding: ${spacing.md} ${spacing.sm} 0;
           overflow: visible;
         }`}
`;

/* StateSwitch is a plain div around a motion div; both need the column's
   width and the fill needs to centre its blocks the way the old
   ContentButtonsWrapper did. */
export const BoardSwitch = styled(StateSwitch)`
  width: 100%;

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

/* A gate fill: heading, then cards or the clock, in a centred column. */
export const GateStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  width: 100%;
`;

/**
 * THE TILES BEFORE THE ANSWER. While the buildings and the entitlement are
 * still being read, the board shows the shape of what is coming — four tile
 * silhouettes in the same grid — rather than nothing and then everything.
 * Same dimensions as PairCard so the real tiles land where these were.
 */
export const TileSkeleton = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 88px;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  ${skeletonBackground}
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * THE CONTAINER THE TWO GRIDS MEASURE THEMSELVES AGAINST.
 *
 * DeskGrid's breakpoints are `@container`, not `@media`, and a container query
 * needs an ancestor that declares itself one. It has to be a wrapper WE own:
 * ContentButtonsWrapper is shared with the wizard, and turning a shared layout
 * element into a containment context changes how everything else inside it
 * resolves.
 *
 * Why the container and not the viewport: this column sits BESIDE the title
 * column, so at 768px of viewport the grid's own share is about 370px. A
 * viewport rule put four columns into that space and pushed the last one off
 * the right edge of the screen — the defect the note on DeskGrid records.
 */
export const DeskArea = styled.div`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  ${deskColumn}
`;

/**
 * THE SUBJECT GRID — FOUR COLUMNS, ONE PER SUBJECT.
 *
 * Owner instruction, 2026-08-13: the four subjects side by side, each carrying
 * In and Out stacked inside it. Visitor, Resident, Parcel, Building Activities
 * are the four things that come in and go out, and putting them on one line
 * makes that symmetry the first thing the screen says.
 *
 * FOUR EXPLICIT COLUMNS, not auto-fit. auto-fit answers "is there room for
 * another tile?" and was right when the grid was a flat list of ten look-alike
 * buttons of unknown length; it is wrong here, because the four are a SET and a
 * 3+1 wrap would read as a group of three plus an afterthought.
 *
 * IT STILL COLLAPSES, and the breakpoints are on the CONTAINER's own space
 * rather than the viewport — this grid sits in a flex column beside the title
 * column, so a viewport rule pushed the last column off the right edge at 768px
 * (see the note this replaces). Four across on a desk, two on a tablet, one on
 * a phone, and In/Out stay side by side inside the card at every width because
 * they are the actual controls.
 */
export const DeskGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${spacing.sm};
  /* Fills DeskArea, which is the thing that holds the width now — see
     deskColumn. Capping the grid instead left it pinned to the left of a
     full-bleed parent while the feed below centred itself. */
  width: 100%;

  @container (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 360px) {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Container queries need a container. Where they are unsupported the fallback
     below keeps the same three steps off the viewport, which is imperfect at the
     column split and never broken. */
  @supports not (container-type: inline-size) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 1100px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    ${down('sm')} {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;

/**
 * THE BOARDS — what you READ rather than what you record.
 *
 * Two columns under the subjects, and a rule above them: Who's home and the
 * Shift Monitor are not errands, and mixing them into the subject grid made six
 * things of two different kinds look like one list of six.
 *
 * "Send the report" used to sit here. It is gone: a report of a shift is
 * produced BY the shift ending, not by somebody remembering to press a button
 * afterwards — see the note on SEND_REPORT_OPTION in main-menu-model.ts.
 */
export const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing.sm};
  width: 100%;
  margin-top: ${spacing.md};
  padding-top: ${spacing.md};
  border-top: 1px solid ${palette.line};

  ${down('sm')} {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Each board sits in a BoardCell (the motion wrapper that staggers it in),
     so the button is one level down from the grid. */
  > div > button {
    width: 100%;
    max-width: none;
    min-height: 64px;
    margin-bottom: 0;
    gap: ${spacing.sm};
  }

  > div > button > div:nth-of-type(1) {
    flex: 0 0 24px;
  }

  > div > button > div:nth-of-type(2) {
    flex: 1 1 auto;
    justify-content: flex-start;
    min-width: 0;
  }

  /* The trailing slot is hidden while EMPTY only: a board with a count (the
     Expected card) shows its badge there. */
  > div > button > div:nth-of-type(3):empty {
    display: none;
  }

  /* The label only, not every span in the button: the count badge is a span
     too, and this rule would have set it to 16px left-aligned. */
  > div > button > div:nth-of-type(2) span {
    text-align: left;
    font-size: ${typography.size.base}px;
    line-height: ${typography.leading.snug};
  }
`;

/* The motion wrapper around one board button: it is what arrives in the
   cascade, since StyledButton is a component and not a motion element. */
export const BoardCell = m.div`
  display: flex;
  min-width: 0;
`;

/**
 * THE GAP BETWEEN THE MENU AND THE FEED.
 *
 * The activity panel is a DIFFERENT KIND OF THING from the grid above it: the
 * grid is ten controls you press, the feed is a record you read. They were
 * siblings in one flex column with the column's own gap between them, so the
 * feed's title sat one small gap under the last row of tiles and the two read as
 * one continuous block — the desk's eye ran straight from a button into a list
 * of names with nothing telling it the subject had changed.
 *
 * A rule AND space, not space alone. On a tall phone the tiles scroll off before
 * the feed arrives, so a gap on its own is a gap between two things you never
 * see together; the hairline is what still says "different thing" when only one
 * of them is on screen.
 *
 * NOT ON THE GATE. It used to render there too — the incoming shift reading
 * what happened before they arrived — and owner instruction of 2026-08-14 ends
 * that: clock in first, then see. Off the clock this slot is not rendered at
 * all, which also gives ClockSlot back its vertical centring (it centres with
 * auto margins, and a sibling below it had nothing to centre against).
 */
export const FeedSlot = styled.section`
  ${deskColumn}
  margin-top: ${spacing.lg};
  padding-top: ${spacing.lg};
  border-top: 1px solid ${palette.line};

  ${down('sm')} {
    margin-top: ${spacing.md};
    padding-top: ${spacing.md};
  }
`;

/**
 * ═══════════ THE CLOCK'S ONE SLOT — under the heading, or alone ═══════════
 *
 * ONE element, ONE position in the tree (MenuBoard's clock cell), two looks:
 *
 *   ON SHIFT ($gate false) it sits in the title column under "Main Menu". It
 *   is not an errand: it is the state of your own shift. In the grid it
 *   competed with the ten controls somebody is trying to pick from and pushed
 *   the first row of tiles down the page. On a phone, where the columns
 *   stack, it lands above the grid — the right order anyway, because the shift
 *   is the frame the errands happen inside. No brand ring here: the clock is
 *   a status to glance at; the things to press are in the grid.
 *
 *   OFF SHIFT ($gate true) it is the only thing on the screen, centred under
 *   the gate's heading, capped at 560px. ShiftCard is a <section>; it is
 *   emphasised with its BORDER and an elevation, not the focus ring —
 *   shadow.focus is a 4px spread ring, and around a card that already draws a
 *   1px border it painted two concentric outlines a few px apart: the "double
 *   border" report (2026-09-02).
 *
 * It USED TO BE TWO ELEMENTS (TitleAside on shift, ClockSlot on the gate) and
 * moving between them was a remount at the exact moment of a handover, which
 * is when ShiftClock's mount effect drops the receipt. Now the card is
 * reconciled in place and only these styles change under it.
 *
 * ALIGNED WITH THE HEADING, AND CLOSE UNDER IT (on shift). The navbar block
 * carries its own horizontal margin (visitor-log.styled NavbarContainer), so a
 * card outside it started 32px further left than "Main Menu"; the matching
 * side margin restores the shared left edge, and the negative top pulls back
 * part of the navbar's own 48px tail so the card sits under the subtitle
 * instead of a screen away from it.
 *
 * No backticks in this comment: it lives inside a styled template literal.
 */
export const ClockSlot = styled.div<{ $gate?: boolean }>`
  grid-area: clock;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  box-sizing: border-box;
  min-width: 0;

  ${({ $gate }) =>
    $gate
      ? `width: 100%;
         max-width: 560px;

         > section {
           width: 100%;
           border-color: ${palette.lagoon};
           box-shadow: ${shadow.md};
         }

         ${down('md')} {
           max-width: none;
         }`
      : `align-items: stretch;
         max-width: 420px;
         margin: -${spacing.lg} ${spacing.lg} 0;

         > section:first-of-type {
           border-color: ${palette.line};
           box-shadow: ${shadow.sm};
         }

         ${down('md')} {
           max-width: none;
           margin: 0 ${spacing.sm} ${spacing.sm};
         }

         @container menuboard (max-width: 599px) {
           max-width: none;
           margin: 0 ${spacing.sm} ${spacing.sm};
         }`}
`;

/**
 * ONE SUBJECT, TWO DIRECTIONS — the Visitor / Resident / Parcel card.
 *
 * Splitting each subject into two look-alike buttons made ten controls out of
 * five subjects, and the desk had to read the whole list to find one of them.
 * The subject is named once, and In and Out sit inside it: pick WHAT, then
 * WHICH WAY, which is the order somebody standing at a counter already thinks
 * in.
 */
export const PairCard = m.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  box-sizing: border-box;
  width: 100%;
  min-height: 88px;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
`;

export const PairHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  min-width: 0;
`;

export const PairLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const PairActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(96px, 100%), 1fr));
  gap: ${spacing.xs};
`;

/**
 * In / Out. Full-height, full-width halves — the whole half is the target, not
 * a word inside it, because this is pressed with a thumb while holding
 * somebody's passport.
 */
export const PairAction = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: 48px;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }

  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

/**
 * ══════════════════ THE GATE IS THE WHOLE SCREEN NOW ══════════════════
 *
 * It went through three shapes, and the first two were both attempts to leave
 * the menu on screen while explaining that most of it did not work:
 *
 *   1. TILES DISABLED. A greyed tile still reads as a tile. The desk pressed
 *      Visitor, nothing happened, and the sentence saying why was in another
 *      column.
 *   2. WORK TILES HIDDEN, read-only tiles kept, plus a banner where the missing
 *      ones had been, plus a notice beside the clock card. Three pieces of
 *      chrome describing one rule, a grid that changed shape depending on a
 *      state nobody could see, and the clock card itself squeezed into a
 *      420px column beside it.
 *   3. THIS. Off the clock there is ONE thing on the screen and it is the
 *      clock. No grid, no banner explaining an absence, nothing to press that
 *      is not the thing to press. Clocking in is not a precondition for the
 *      menu — off shift, it IS the menu.
 *
 * Owner instruction, 2026-08-11: "no button should appear till the concierge /
 * admin / manager clocks in, once they clock in they can manage the logs."
 *
 * The centred column is capped at 560px: wide enough for the clock card to
 * breathe (it holds a resolve form in the blocked state), narrow enough that
 * the heading and the primary action stay in one gaze on a desk monitor.
 */
/* The gate's heading. It replaces "Main Menu — please select one of the options
   below", which is a false sentence when there are no options. */
export const GateHead = styled.div<{ $gate?: boolean }>`
  display: block;
  width: 100%;
  text-align: center;
`;

/* <h1>, NOT <h2>. When a gate is up it REPLACES the menu and its title column
   (main-menu.tsx renders the navbar only when neither gate is shown), so this is
   the page's only heading — and it was starting the outline at level 2 with no
   level 1 above it. The two gates that use it, "Choose a building" and "Start
   your shift", are each the whole screen at the moment they appear.

   Purely semantic: every property the browser would apply to an h1 (size,
   weight, margin) is already set below. */
export const GateTitle = styled.h1`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

export const GateText = styled.p`
  max-width: 46ch;
  margin: ${spacing.half} auto 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* ── THE BUILDING GATE (WP-07 / BUG-015, ruling R-5) ─────────────────────────
   Same treatment as the clock gate one level up: if you cover more than one
   building, choosing one IS the screen, and it comes before the menu.

   Pressable cards rather than a select. A select hides its options behind a
   click and reads as a filter on a screen that already exists; this screen
   exists only to make the choice, so the choices are the screen. It is also the
   pattern the clock gate already set on the surface immediately after this one.

   NO BACKTICKS IN THESE COMMENTS: they are inside styled template literals. */
export const BuildingGateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${spacing.sm};
  width: 100%;
  margin-top: ${spacing.sm};
`;

export const BuildingGateCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  text-align: left;
  min-height: ${layout.touchTarget};
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  cursor: pointer;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring},
    box-shadow ${duration.fast} ${easing.spring};

  &:hover {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.md};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('sm')} {
    padding: ${spacing.sm};
  }

  /* A card that lifts on hover is a card that moves under a finger that is
     already on its way down. */
  @media (prefers-reduced-motion: reduce) {
    transition: ${transition.colors};

    &:hover {
      transform: none;
    }
  }
`;

export const BuildingGateName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const BuildingGateMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;
